import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  Copy,
  Globe,
  Image as ImageIcon,
  Layers,
  MessageCircle,
  Plus,
  Send,
  ShoppingBag,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Input, SegmentedControl, Select, Slider } from "@/components/ui/Input";
import { useToast } from "@/components/ui/Toast";
import { useShell } from "@/components/Shell";
import { SettingsLayout, FormSection, FormRow } from "@/routes/Settings/SettingsLayout";
import {
  getOverview,
  updateSettings,
  uploadAvatar,
  type Campground,
  type WidgetConfig,
} from "@/lib/api";
import { loadSession } from "@/lib/session";
import { WIDGET_SCRIPT_URL } from "@/lib/config";
import { cn } from "@/lib/cn";

const AVATAR_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/svg+xml";
const AVATAR_MAX_BYTES = 2 * 1024 * 1024; // matches the server bucket cap.

/**
 * Widget customizer.
 *
 * Two-column layout (form left, live preview right) on >=lg, stacked below.
 * Everything in the form re-renders the right-hand mock immediately so the
 * owner can see exactly what their visitors will see before saving.
 *
 * `widget_config` is a partial-replace JSON column shared with the Bot and
 * Domains pages, so we always merge over the loaded payload before sending.
 */

// Build-time configurable so a fork of the project can ship its own widget
// bundle without touching this file. See dashboard/src/lib/config.ts.
const WIDGET_SRC = WIDGET_SCRIPT_URL;

const DEFAULT_COLOR = "#FF5252";
const DEFAULT_POSITION: "right" | "left" = "right";
const DEFAULT_INITIAL_STATE: "collapsed" | "open" | "proactive" = "collapsed";
const DEFAULT_LOCALE = "en-US";
const DEFAULT_TITLE = "Chat with us";
const DEFAULT_PROACTIVE_MS = 4000;
const MAX_SUGGESTIONS = 6;

interface OwnedConfig {
  color_primary: string;
  position: "right" | "left";
  title: string;
  initial_state: "collapsed" | "open" | "proactive";
  proactive_message: string;
  proactive_delay_ms: number;
  locale: string;
  avatar_url: string;
  suggested_questions: string[];
}

function readOwned(cfg: WidgetConfig | undefined | null): OwnedConfig {
  return {
    color_primary:       cfg?.color_primary       ?? DEFAULT_COLOR,
    position:            cfg?.position            ?? DEFAULT_POSITION,
    title:               cfg?.title               ?? DEFAULT_TITLE,
    initial_state:       cfg?.initial_state       ?? DEFAULT_INITIAL_STATE,
    proactive_message:   cfg?.proactive_message   ?? "",
    proactive_delay_ms:  cfg?.proactive_delay_ms  ?? DEFAULT_PROACTIVE_MS,
    locale:              cfg?.locale              ?? DEFAULT_LOCALE,
    avatar_url:          cfg?.avatar_url          ?? "",
    suggested_questions: Array.isArray(cfg?.suggested_questions) ? cfg!.suggested_questions! : [],
  };
}

function ownedEqual(a: OwnedConfig, b: OwnedConfig): boolean {
  return (
    a.color_primary === b.color_primary &&
    a.position === b.position &&
    a.title === b.title &&
    a.initial_state === b.initial_state &&
    a.proactive_message === b.proactive_message &&
    a.proactive_delay_ms === b.proactive_delay_ms &&
    a.locale === b.locale &&
    a.avatar_url === b.avatar_url &&
    a.suggested_questions.length === b.suggested_questions.length &&
    a.suggested_questions.every((q, i) => q === b.suggested_questions[i])
  );
}

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
function isValidHex(s: string): boolean {
  return HEX_RE.test(s.trim());
}

const LOCALES = [
  { value: "en-US", label: "English (en-US)" },
  { value: "es-AR", label: "Spanish (es-AR)" },
  { value: "pt-BR", label: "Portuguese (pt-BR)" },
  { value: "fr-FR", label: "French (fr-FR)" },
];

export function Widget() {
  const { refresh: refreshShell } = useShell();
  const toast = useToast();

  const [campground, setCampground] = useState<Campground | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Baseline (server state, normalized to defaults) and the working copy.
  const [baseline, setBaseline] = useState<OwnedConfig>(() => readOwned(undefined));
  const [draft, setDraft] = useState<OwnedConfig>(() => readOwned(undefined));
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getOverview();
      setCampground(r.campground);
      const b = readOwned(r.campground.widget_config);
      setBaseline(b);
      setDraft(b);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const dirty = useMemo(() => !ownedEqual(baseline, draft), [baseline, draft]);

  const onSave = async () => {
    if (!campground) return;
    setSaving(true);
    try {
      // Merge: start from full server widget_config, overwrite the keys this
      // page owns. Never clobber sibling keys (personality_preset, fallback_*,
      // recent_origin_rejections, etc.).
      const merged: WidgetConfig & Record<string, unknown> = {
        ...(campground.widget_config ?? {}),
        color_primary:       draft.color_primary,
        position:            draft.position,
        title:               draft.title,
        initial_state:       draft.initial_state,
        proactive_message:   draft.proactive_message,
        proactive_delay_ms:  draft.proactive_delay_ms,
        locale:              draft.locale,
        avatar_url:          draft.avatar_url,
        suggested_questions: draft.suggested_questions,
      };
      const r = await updateSettings({ widget_config: merged });
      setCampground(r.campground);
      const b = readOwned(r.campground.widget_config);
      setBaseline(b);
      setDraft(b);
      void refreshShell();
      toast.push("Widget settings saved.");
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const onDiscard = () => setDraft(baseline);

  // ---- render ----------------------------------------------------------

  if (loading && !campground) {
    return (
      <SettingsLayout
        title="Widget customizer"
        description="Visual customization with a live preview and embed snippet."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <FormSectionSkeleton rows={3} />
            <FormSectionSkeleton rows={2} />
            <FormSectionSkeleton rows={2} />
          </div>
          <div>
            <Card className="sticky top-8">
              <CardBody>
                <Skeleton className="h-[520px] w-full" />
              </CardBody>
            </Card>
          </div>
        </div>
      </SettingsLayout>
    );
  }

  if (error) {
    return (
      <SettingsLayout
        title="Widget customizer"
        description="Visual customization with a live preview and embed snippet."
      >
        <Card>
          <CardBody className="flex items-start gap-3">
            <AlertCircle size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
            <div className="flex-1">
              <p className="text-sm text-text-strong">Couldn't load widget settings.</p>
              <p className="mt-1 text-xs text-text-soft">{error}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => void load()}>
              Try again
            </Button>
          </CardBody>
        </Card>
      </SettingsLayout>
    );
  }

  if (!campground) return null;

  const welcomeMessage =
    (campground.widget_config?.welcome_message as string | undefined) ?? "";

  const session = loadSession();
  const snippet = buildSnippet({
    publicKey: campground.public_key,
    supabaseUrl: session?.url ?? "",
  });

  const saveBar = dirty ? (
    <>
      <span className="text-xs text-text-soft">You have unsaved changes.</span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button variant="primary" size="sm" loading={saving} onClick={() => void onSave()}>
          Save changes
        </Button>
      </div>
    </>
  ) : null;

  return (
    <SettingsLayout
      title="Widget customizer"
      description="Visual customization with a live preview and embed snippet."
      saveBar={saveBar}
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        {/* Left column ---------------------------------------------------- */}
        <div className="space-y-6">
          <FormSection
            title="Branding"
            description="Color, locale, and the avatar shown in the chat header."
          >
            <FormRow
              label="Primary color"
              hint="Used for the bubble, header, and primary buttons."
              htmlFor="widget-color"
            >
              <div className="flex items-center gap-2">
                <Input
                  id="widget-color"
                  type="color"
                  value={isValidHex(draft.color_primary) ? draft.color_primary : DEFAULT_COLOR}
                  onChange={(e) => setDraft((d) => ({ ...d, color_primary: e.target.value }))}
                  className="h-9 w-12 cursor-pointer p-1"
                />
                <Input
                  type="text"
                  value={draft.color_primary}
                  onChange={(e) => setDraft((d) => ({ ...d, color_primary: e.target.value }))}
                  placeholder="#FF5252"
                  className={cn("w-32 tabular-nums", !isValidHex(draft.color_primary) && "border-danger")}
                />
                <span
                  className="inline-block h-6 w-6 rounded border border-line"
                  style={{ backgroundColor: isValidHex(draft.color_primary) ? draft.color_primary : "transparent" }}
                  aria-hidden
                />
              </div>
              {!isValidHex(draft.color_primary) && (
                <p className="mt-1.5 text-xs text-danger">Enter a valid hex like #FF5252.</p>
              )}
            </FormRow>

            <FormRow
              label="Avatar"
              hint="Square image displayed in the chat header. Paste a URL or upload PNG/JPG/WebP/GIF/SVG up to 2 MB. Leave empty to use the default."
              htmlFor="widget-avatar"
            >
              <AvatarField
                value={draft.avatar_url}
                onChange={(v) => setDraft((d) => ({ ...d, avatar_url: v }))}
              />
            </FormRow>

            <FormRow
              label="Locale"
              hint="Default language for built-in widget strings."
              htmlFor="widget-locale"
            >
              <Select
                id="widget-locale"
                value={draft.locale}
                onChange={(e) => setDraft((d) => ({ ...d, locale: e.target.value }))}
                className="max-w-xs"
              >
                {LOCALES.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </Select>
            </FormRow>
          </FormSection>

          <FormSection
            title="Position and initial state"
            description="Where the bubble sits and how it greets a first-time visitor."
          >
            <FormRow label="Position" hint="Side of the viewport the bubble anchors to.">
              <SegmentedControl
                value={draft.position}
                onChange={(v) => setDraft((d) => ({ ...d, position: v }))}
                options={[
                  { value: "right", label: "Right" },
                  { value: "left",  label: "Left"  },
                ]}
              />
            </FormRow>

            <FormRow label="Initial state" hint="What the visitor sees before any interaction.">
              <SegmentedControl
                value={draft.initial_state}
                onChange={(v) => setDraft((d) => ({ ...d, initial_state: v }))}
                options={[
                  { value: "collapsed", label: "Collapsed bubble" },
                  { value: "open",      label: "Open"             },
                  { value: "proactive", label: "Proactive popup"  },
                ]}
              />
            </FormRow>

            {draft.initial_state === "proactive" && (
              <>
                <FormRow
                  label="Proactive message"
                  hint="Shown in a tooltip above the bubble after the delay."
                  htmlFor="widget-proactive-msg"
                >
                  <Input
                    id="widget-proactive-msg"
                    type="text"
                    placeholder="Need help finding a site?"
                    value={draft.proactive_message}
                    onChange={(e) => setDraft((d) => ({ ...d, proactive_message: e.target.value }))}
                  />
                </FormRow>
                <FormRow
                  label="Delay"
                  hint="Seconds after page load before the popup appears."
                  htmlFor="widget-proactive-delay"
                >
                  <Slider
                    id="widget-proactive-delay"
                    value={Math.round(draft.proactive_delay_ms / 1000)}
                    onChange={(v) => setDraft((d) => ({ ...d, proactive_delay_ms: v * 1000 }))}
                    min={0}
                    max={30}
                    step={1}
                    unit="s"
                  />
                </FormRow>
              </>
            )}
          </FormSection>

          <FormSection
            title="Chat panel content"
            description="What greets the visitor once the panel is open."
          >
            <FormRow label="Header title" hint="Header text in the chat panel." htmlFor="widget-title">
              <Input
                id="widget-title"
                type="text"
                placeholder="Chat with us"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                className="max-w-md"
              />
            </FormRow>

            <FormRow
              label="Suggested questions"
              hint={`Up to ${MAX_SUGGESTIONS} starter prompts shown under the welcome message. The welcome message itself lives on the bot page.`}
              layout="stacked"
            >
              <div className="space-y-2">
                {draft.suggested_questions.length === 0 && (
                  <p className="text-xs text-text-soft">No suggestions yet — add one below.</p>
                )}
                {draft.suggested_questions.map((q, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={q}
                      placeholder="e.g. Do you have pet-friendly sites?"
                      onChange={(e) =>
                        setDraft((d) => {
                          const next = [...d.suggested_questions];
                          next[i] = e.target.value;
                          return { ...d, suggested_questions: next };
                        })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Remove suggestion"
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          suggested_questions: d.suggested_questions.filter((_, j) => j !== i),
                        }))
                      }
                    >
                      <X size={14} strokeWidth={1.75} />
                    </Button>
                  </div>
                ))}
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={<Plus size={12} strokeWidth={1.75} />}
                    disabled={draft.suggested_questions.length >= MAX_SUGGESTIONS}
                    onClick={() =>
                      setDraft((d) => ({
                        ...d,
                        suggested_questions: [...d.suggested_questions, ""],
                      }))
                    }
                  >
                    Add suggestion
                  </Button>
                  {draft.suggested_questions.length >= MAX_SUGGESTIONS && (
                    <span className="ml-2 text-xs text-text-faint">
                      Maximum of {MAX_SUGGESTIONS} reached.
                    </span>
                  )}
                </div>
              </div>
            </FormRow>
          </FormSection>
        </div>

        {/* Right column — live preview ----------------------------------- */}
        <div>
          <div className="sticky top-8">
            <Card>
              <CardHeader
                title="Live preview"
                subtitle="Approximation of the embedded widget. Click the bubble to open it."
              />
              <CardBody>
                <WidgetPreview draft={draft} welcomeMessage={welcomeMessage} />
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      {/* Install snippet (full width) ------------------------------------ */}
      <FormSection
        title="Install snippet"
        description="Paste this once in the page's HTML head. Works on any site."
      >
        <div className="space-y-3 px-5 py-4">
          <pre className="overflow-x-auto whitespace-pre rounded bg-ink p-3 text-xs text-white/85">
{snippet}
          </pre>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-text-soft">
              The script reads <code className="text-text">data-public-key</code> and{" "}
              <code className="text-text">data-supabase-url</code> attributes, so this is the only
              tag you need.
            </p>
            <CopySnippetButton snippet={snippet} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 px-5 pb-5 sm:grid-cols-3">
          <InstallCard
            icon={Globe}
            title="WordPress"
            text='Paste in the <head> via a header-scripts plugin (e.g. Insert Headers and Footers).'
          />
          <InstallCard
            icon={ShoppingBag}
            title="Wix"
            text="Settings → Custom Code → Add Custom Code → place in the <head>."
          />
          <InstallCard
            icon={Layers}
            title="Squarespace"
            text="Settings → Advanced → Code Injection → Header."
          />
        </div>
      </FormSection>
    </SettingsLayout>
  );
}

// ---------------------------------------------------------------------------
// Live preview
// ---------------------------------------------------------------------------

interface WidgetPreviewProps {
  draft: OwnedConfig;
  welcomeMessage: string;
}

function WidgetPreview({ draft, welcomeMessage }: WidgetPreviewProps) {
  const color = isValidHex(draft.color_primary) ? draft.color_primary : DEFAULT_COLOR;
  const sideRight = draft.position === "right";
  const [open, setOpen] = useState(draft.initial_state === "open");

  // Reflect the configured initial state, but let the user click the bubble
  // to open/close the panel and see exactly how it looks either way.
  useEffect(() => { setOpen(draft.initial_state === "open"); }, [draft.initial_state]);

  return (
    <div
      className="relative mx-auto h-[520px] w-full max-w-[320px] overflow-hidden rounded border border-line"
      style={{
        backgroundColor: "var(--surface-2-fallback, #f3f4f6)",
        // Coarse dot grid to suggest "this is a website background".
        backgroundImage:
          "radial-gradient(rgba(0,0,0,0.08) 1px, transparent 1px)",
        backgroundSize: "14px 14px",
      }}
    >
      {/* Bubble — click to open the panel. Hidden while open (the panel
          covers this corner, same as the real widget). */}
      {!open && (
        <button
          type="button"
          aria-label="Open chat"
          onClick={() => setOpen(true)}
          className={cn(
            "absolute bottom-3 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-sm transition-transform hover:scale-105",
            sideRight ? "right-3" : "left-3",
          )}
          style={{ backgroundColor: color }}
        >
          <MessageCircle size={20} strokeWidth={1.75} />
        </button>
      )}

      {!open && draft.initial_state === "proactive" && draft.proactive_message.trim() && (
        <div
          className={cn(
            "absolute bottom-[68px] max-w-[230px] rounded border border-line bg-surface px-3 py-2 text-xs text-text-strong",
            sideRight ? "right-3" : "left-3",
          )}
        >
          {draft.proactive_message}
          <span
            className={cn(
              "absolute -bottom-1 h-2 w-2 rotate-45 border-b border-r border-line bg-surface",
              sideRight ? "right-4" : "left-4",
            )}
          />
        </div>
      )}

      {open && (
        <div
          className={cn(
            "absolute bottom-3 flex h-[480px] w-[280px] flex-col overflow-hidden rounded border border-line bg-surface",
            sideRight ? "right-3" : "left-3",
          )}
        >
          {/* Header */}
          <div
            className="flex items-center gap-2 px-3 py-2.5 text-white"
            style={{ backgroundColor: color }}
          >
            <PreviewAvatar url={draft.avatar_url} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium leading-tight">
                {draft.title || DEFAULT_TITLE}
              </div>
              <div className="text-[10px] leading-tight opacity-80">Usually replies in minutes</div>
            </div>
            <button
              type="button"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              className="opacity-80 transition-opacity hover:opacity-100"
            >
              <X size={14} strokeWidth={1.75} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 space-y-2 overflow-y-auto bg-surface-2 px-3 py-3">
            <div className="max-w-[80%] rounded rounded-bl-sm bg-surface px-3 py-2 text-xs text-text-strong">
              {welcomeMessage?.trim() ? welcomeMessage : "Hi! How can I help you today?"}
            </div>
            {draft.suggested_questions.filter((q) => q.trim()).length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {draft.suggested_questions
                  .filter((q) => q.trim())
                  .slice(0, MAX_SUGGESTIONS)
                  .map((q, i) => (
                    <span
                      key={i}
                      className="rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] text-text-strong"
                      style={{ borderColor: color }}
                    >
                      {q}
                    </span>
                  ))}
              </div>
            )}
          </div>

          {/* Footer / disabled composer */}
          <div className="flex items-center gap-2 border-t border-line bg-surface px-2.5 py-2">
            <div className="flex-1 rounded border border-line bg-surface-2 px-2.5 py-1.5 text-[11px] text-text-faint">
              Type a message…
            </div>
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded text-white"
              style={{ backgroundColor: color }}
              tabIndex={-1}
              aria-hidden
            >
              <Send size={12} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Avatar field: URL input + file upload, with a thumbnail next to them.
 * The two inputs share one underlying string — uploading just rewrites the
 * URL to the public bucket location returned by the edge function.
 */
function AvatarField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [thumbBroken, setThumbBroken] = useState(false);

  // Reset the "broken image" flag whenever the URL changes so a previously
  // failed thumbnail can recover after the owner pastes a working URL.
  useEffect(() => { setThumbBroken(false); }, [value]);

  const onPick = () => inputRef.current?.click();

  const onFile = async (file: File | null | undefined) => {
    if (!file) return;
    if (file.size > AVATAR_MAX_BYTES) {
      toast.push(`Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 2 MB.`, "error");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadAvatar(file);
      onChange(res.url);
      toast.push("Avatar uploaded. Don't forget to save.");
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Upload failed", "error");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const trimmed = value.trim();
  const hasValue = trimmed.length > 0;
  const showThumb = hasValue && !thumbBroken;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {/* Thumbnail */}
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border border-line bg-surface-2",
          )}
          aria-hidden
        >
          {showThumb ? (
            <img
              src={trimmed}
              alt=""
              className="h-full w-full object-cover"
              onError={() => setThumbBroken(true)}
            />
          ) : (
            <ImageIcon size={16} strokeWidth={1.75} className="text-text-faint" />
          )}
        </div>

        <Input
          id="widget-avatar"
          type="url"
          placeholder="https://example.com/avatar.png"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1"
        />

        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onPick}
          loading={uploading}
          iconLeft={!uploading && <Upload size={12} strokeWidth={1.75} />}
        >
          {uploading ? "Uploading…" : "Upload"}
        </Button>

        {hasValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange("")}
            disabled={uploading}
            aria-label="Remove avatar"
          >
            <Trash2 size={12} strokeWidth={1.75} />
          </Button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={AVATAR_ACCEPT}
          className="hidden"
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
      </div>

      {hasValue && thumbBroken && (
        <p className="text-xs text-warning">
          That URL didn't load — make sure the image is public.
        </p>
      )}
    </div>
  );
}

function PreviewAvatar({ url }: { url: string }) {
  const valid = url.trim().length > 0;
  if (valid) {
    return (
      <img
        src={url}
        alt=""
        className="h-7 w-7 shrink-0 rounded-full border border-white/30 object-cover"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = "none";
        }}
      />
    );
  }
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20">
      <Sparkles size={12} strokeWidth={1.75} className="text-white" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Install snippet helpers
// ---------------------------------------------------------------------------

function buildSnippet({ publicKey, supabaseUrl }: { publicKey: string; supabaseUrl: string }): string {
  return `<script
  src="${WIDGET_SRC}"
  data-public-key="${publicKey}"
  data-supabase-url="${supabaseUrl}"
  async></script>`;
}

function CopySnippetButton({ snippet }: { snippet: string }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.push("Snippet copied to clipboard.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.push("Couldn't access clipboard.", "error");
    }
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => void onCopy()}
      iconLeft={
        copied
          ? <Check size={12} strokeWidth={1.75} />
          : <Copy size={12} strokeWidth={1.75} />
      }
    >
      {copied ? "Copied" : "Copy snippet"}
    </Button>
  );
}

function InstallCard({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof Globe;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded border border-line bg-surface p-3">
      <div className="flex items-center gap-2">
        <Icon size={14} strokeWidth={1.75} className="text-text-soft" />
        <span className="text-sm font-medium text-text-strong">{title}</span>
      </div>
      <p className="mt-1.5 text-xs text-text-soft">{text}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton helpers
// ---------------------------------------------------------------------------

function FormSectionSkeleton({ rows }: { rows: number }) {
  return (
    <section className="card">
      <div className="border-b border-line px-5 py-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-2 h-3 w-56" />
      </div>
      <div className="divide-y divide-line">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-[1fr_2fr]">
            <div>
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-2 h-3 w-40" />
            </div>
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    </section>
  );
}

// Suppress unused warning for the icon import kept for potential future use.
void ImageIcon;
